import { AbstractEntitiesTableComponent } from '@abstracts/entities/entities-table';
import { DecimalPipe } from '@angular/common';
import { Component, EventEmitter, Injector, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { TransactionDto } from '@dto/transactionDto';
import { DeliveryNote } from '@models/deliveryNote';
import { TableColumn } from '@models/tableColumn';
import { TableOptions } from '@models/tableOptions';
import { Transaction } from '@models/transaction';
import { DeliveryNotesService } from '@services/entity/delivery-notes.service';
import { TransactionsService } from '@services/entity/transactions.service';
import { FormService } from '@services/form/form.service';
import { CustomersService } from '@services/entity/customers.service'
import { ToastService } from '@services/toast/toast.service';
import { DEDatePipe } from 'src/app/pipes/de-date.pipe';
import { ThousandPipe } from 'src/app/pipes/thousand.pipe';
import Swal from 'sweetalert2';
import { CustomerDto } from '../../../../dto/customerDto';
import { UsersService } from '@services/entity/users.service';
import { UserDto } from '../../../../dto/userDto';

@Component({
  selector: 'app-create-delivery-csv',
  templateUrl: './create-delivery-csv.component.html',
  styleUrls: ['./create-delivery-csv.component.scss']
})
export class CreateDeliveryCSVComponent extends AbstractEntitiesTableComponent implements OnInit, OnChanges {

  public className = 'CreateDeliveryCSVComponent';

  @Input() public transactions: TransactionDto[];
  @Input() public userId: string;
  @Input() public customerId: number;

  @Output() newEntityEvent = new EventEmitter<DeliveryNote>();

  public entities: TransactionDto[];

  public tableColumns: TableColumn[] = [
    { columnDescription: $localize`:@@CreateDeliveryNoteTableColumn1:Trans-ID`, property: 'transactionId' },
    { columnDescription: $localize`:@@CreateDeliveryNoteTableColumn2:Vorname`, property: 'userFirstName' },
    { columnDescription: $localize`:@@CreateDeliveryNoteTableColumn3:Nachname`, property: 'userLastName' },
    { columnDescription: $localize`:@@CreateDeliveryNoteTableColumn4:Ladestation`, property: 'chargingStationName' },
    { columnDescription: $localize`:@@CreateDeliveryNoteTableColumn5:Lademenge in kWh`, property: 'meterAmount', options: { columnPipe: { pipe: new ThousandPipe(new DecimalPipe('de-DE')) } } },
    { columnDescription: $localize`:@@CreateDeliveryNoteTableColumn6:Datum`, property: 'startTimestamp', options: { columnPipe: { pipe: new DEDatePipe() } } },
  ];

  public tableOptions: TableOptions = { hideActions: true, hideSearch: true, showCheckboxes: true };

  public exportRows: TransactionDto[];
  public customer: UserDto;
  public user: UserDto;
  private readonly noTransactionsSelectedMessage = 'Keine Transaktionen ausgewählt.';
  private readonly noTransactionsSelectedMessageNormalized = 'keine transaktionen ausgewahlt';
  private isInitialized = false;

  constructor(
    public deliveryNotesService: DeliveryNotesService,
    public customerService: CustomersService,
    public usersService: UsersService,
    public formService: FormService,
    public toastService: ToastService,
    injector: Injector
  ) {
    super(deliveryNotesService, injector);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.tableFunctions.pageSize = 999999;
    this.tableFunctions.sortColumn = 'transactionId';
    this.tableFunctions.sortDirection = 'desc';
    this.createForm();
    this.isInitialized = true;
    this.applyTransactionsToTable();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['transactions']) {
      return;
    }

    this.entities = this.transactions ?? [];
    if (this.isInitialized) {
      this.applyTransactionsToTable();
    }
  }

  initializeEntities() {
    this.entities = this.transactions ?? [];
    super.setupTable();
  }

  private applyTransactionsToTable(): void {
    this.entities = this.transactions ?? [];
    this.tableFunctions.selectedEntities = [...this.entities];
    super.setupTable();
  }

  getSimulatedUser(t: TransactionDto) {
    if (t.isFreeCharge) return 'freecharge';
    if (t.paymentIntentId) return 'QRCodecharge';
    if (t.sessionId) return 'ERoamingcharge';
    if (t.hecId) return 'Terminalcharge';
    return null;
  }

  matches(entity: TransactionDto, term: string) {
    return TransactionsService.matches(entity, term);
  }

  createForm() {
    this.formService.form = new UntypedFormGroup({
      deliveryNoteNumber: new UntypedFormControl(),
    });
  }

  onSubmit(formValue, download: boolean = false) {
    download = true;
    var deliveryNote = new DeliveryNote(formValue);
    const selectedTransactions = (this.tableFunctions.selectedEntities ?? []) as Transaction[];
    if (selectedTransactions.length === 0) {
      this.toastService.warn(this.noTransactionsSelectedMessage);
      return;
    }
    deliveryNote.transactions = selectedTransactions;
    deliveryNote.customerId = this.customerId;

    this.deliveryNotesService.createEntity(deliveryNote).subscribe(result => {
      this.newEntityEvent.emit(deliveryNote);
      if(download)
       this.downloadCSV(result.id, result.deliveryNoteNumber);
    }, error => {
      if (this.isNoTransactionsSelectedError(error)) {
        this.toastService.warn(this.noTransactionsSelectedMessage);
      } else {
        this.toastService.error($localize`:@@CreateDeliveryNoteErrorTitle:Fehler beim Erstellen der CSV-Datei.`);
      }
      this.messageService.setError(error);
    })

  }

  private isNoTransactionsSelectedError(error: any): boolean {
    const possibleMessages: string[] = [
      typeof error === 'string' ? error : null,
      error?.error,
      error?.error?.errorMessage,
      error?.error?.message,
      error?.message,
      error?.statusText
    ].filter((value): value is string => typeof value === 'string' && value.length > 0);

    return possibleMessages.some(message =>
      this.normalizeMessage(message).includes(this.noTransactionsSelectedMessageNormalized)
    );
  }

  private normalizeMessage(message: string): string {
    return message
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\./g, '')
      .trim();
  }

  downloadCSV(deliveryNoteId:number, deliveryNoteNumber:string ) {
    this.toastService.success(`CSV-Datei wird heruntergeladen...`,2500,true)
    console.log("deliveryNoteId",deliveryNoteId);
    console.log("deliveryNoteNumber",deliveryNoteNumber);

    this.deliveryNotesService.getDeliveryNoteAsCSV(deliveryNoteId).subscribe((response: any) => {
      try {
        const file = new Blob([response], { type: 'text/csv' });
        const fileUrl = URL.createObjectURL(file);

        var a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';
        a.href = fileUrl;
        a.download = deliveryNoteNumber;
        a.click();
      } catch (error) {
        console.error(error);
        this.toastService.error(`CSV-Datei konnte nicht heruntergeladen werden.`, 0, true)
      } finally {
        this.toastService.clear()
      }
    }, error => {
      console.error(error);
      this.toastService.error(`CSV-Datei konnte nicht heruntergeladen werden.`, 0, true)
    })

  }

}
